import { useMemo } from "react";
import {
    type ConsentGovernanceVersion,
    useGetAdminConsentItemVersions,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { getConsentItemId } from "../utils/consentVersionModalModel";

type UseConsentVersionLocalizedQueriesOptions = {
    consentVersion?: ConsentGovernanceVersion;
    editingLocale: string;
    isDefaultEditingLocale: boolean;
    open: boolean;
};

export const useConsentVersionLocalizedQueries = ({
    consentVersion,
    editingLocale,
    isDefaultEditingLocale,
    open,
}: UseConsentVersionLocalizedQueriesOptions) => {
    const consentVersionItemId = getConsentItemId(consentVersion?.consentItem);
    const baseQueryParams = {
        "consentItem.id": consentVersionItemId,
        version: consentVersion?.version,
    };
    const { consentItemVersions: localizedConsentVersions = [], isFetching: isLocalizedConsentVersionsFetching } =
        useGetAdminConsentItemVersions({
            enabled: open && consentVersion !== undefined,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                ...baseQueryParams,
                "order[id]": "asc",
            },
        });
    const { consentItemVersions: translatedTitleConsentVersions = [], isFetching: isTranslatedTitleFetching } =
        useGetAdminConsentItemVersions({
            enabled: open && consentVersion !== undefined && !isDefaultEditingLocale,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                ...baseQueryParams,
                contentLocale: editingLocale,
                contentLocaleFields: "title",
            },
        });
    const { consentItemVersions: translatedSummaryConsentVersions = [], isFetching: isTranslatedSummaryFetching } =
        useGetAdminConsentItemVersions({
            enabled: open && consentVersion !== undefined && !isDefaultEditingLocale,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                ...baseQueryParams,
                contentLocale: editingLocale,
                contentLocaleFields: "summary",
            },
        });
    const { consentItemVersions: translatedContentConsentVersions = [], isFetching: isTranslatedContentFetching } =
        useGetAdminConsentItemVersions({
            enabled: open && consentVersion !== undefined && !isDefaultEditingLocale,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                ...baseQueryParams,
                contentLocale: editingLocale,
                contentLocaleFields: "content",
            },
        });
    const {
        consentItemVersions: translatedDocumentUrlConsentVersions = [],
        isFetching: isTranslatedDocumentUrlFetching,
    } = useGetAdminConsentItemVersions({
        enabled: open && consentVersion !== undefined && !isDefaultEditingLocale,
        itemsPerPage: 200,
        locale: editingLocale,
        queryParams: {
            ...baseQueryParams,
            contentLocale: editingLocale,
            contentLocaleFields: "documentUrl",
        },
    });

    const localizedConsentVersion = useMemo(
        () => localizedConsentVersions.find((version) => version.id === consentVersion?.id),
        [consentVersion?.id, localizedConsentVersions],
    );
    const hasTranslatedVersionField = (versions: ConsentGovernanceVersion[]) =>
        versions.some((version) => version.id === consentVersion?.id);

    const hasLocalizedTitle =
        consentVersion === undefined ||
        isDefaultEditingLocale ||
        hasTranslatedVersionField(translatedTitleConsentVersions);
    const hasLocalizedSummary =
        consentVersion === undefined ||
        isDefaultEditingLocale ||
        hasTranslatedVersionField(translatedSummaryConsentVersions);
    const hasLocalizedContent =
        consentVersion === undefined ||
        isDefaultEditingLocale ||
        hasTranslatedVersionField(translatedContentConsentVersions);
    const hasLocalizedDocumentUrl =
        consentVersion === undefined ||
        isDefaultEditingLocale ||
        hasTranslatedVersionField(translatedDocumentUrlConsentVersions);

    return {
        hasAnyLocalizedConsentVersionField:
            hasLocalizedTitle || hasLocalizedSummary || hasLocalizedContent || hasLocalizedDocumentUrl,
        hasLocalizedContent,
        hasLocalizedDocumentUrl,
        hasLocalizedSummary,
        hasLocalizedTitle,
        isLocalizedContentFetching:
            isLocalizedConsentVersionsFetching ||
            isTranslatedTitleFetching ||
            isTranslatedSummaryFetching ||
            isTranslatedContentFetching ||
            isTranslatedDocumentUrlFetching,
        localizedConsentVersion,
    };
};
